/**
 * Apresentação da central de notificações do Portal do Credenciado: rótulo e
 * cor de prioridade, tempo relativo e agrupamento por data.
 *
 * O CONTRATO DO DADO não mora aqui: a caixa é a mesma de todas as áreas
 * (`modulos/notificacoes/contrato.ts`, `bff/v1/notificacoes`). Os tipos do
 * protótipo (vencimento de documento, nova cobrança, status de solicitação,
 * comunicado geral) saíram em 2026-10-03: o tipo é o código que o banco grava.
 */

import type {
  CategoriaDeNotificacao,
  PrioridadeDeNotificacao,
} from '@/modulos/notificacoes/contrato';

export type { CategoriaDeNotificacao, PrioridadeDeNotificacao };

export const PRIORIDADES_DE_NOTIFICACAO: ReadonlyArray<{
  value: PrioridadeDeNotificacao;
  label: string;
}> = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
];

/** Token semântico do indicador de prioridade (ponto colorido no card da notificação). */
export const TOKEN_DE_PRIORIDADE: Record<PrioridadeDeNotificacao, string> = {
  alta: 'bg-destructive',
  media: 'bg-warning',
  baixa: 'bg-muted-foreground',
};

const ROTULO_DA_PRIORIDADE: Record<PrioridadeDeNotificacao, string> = Object.fromEntries(
  PRIORIDADES_DE_NOTIFICACAO.map((opcao) => [opcao.value, opcao.label]),
) as Record<PrioridadeDeNotificacao, string>;

export function rotuloDaPrioridade(prioridade: PrioridadeDeNotificacao): string {
  return ROTULO_DA_PRIORIDADE[prioridade];
}

/** Tempo relativo curto ("agora", "há 2 dias"), para a lista de notificações. */
export function tempoRelativo(dataIso: string): string {
  const diferencaMs = Date.now() - new Date(dataIso).getTime();
  const diferencaMin = Math.round(diferencaMs / 60_000);

  if (diferencaMin < 1) return 'agora';
  if (diferencaMin < 60) return `há ${diferencaMin} min`;

  const diferencaHoras = Math.round(diferencaMin / 60);
  if (diferencaHoras < 24) return `há ${diferencaHoras}h`;

  const diferencaDias = Math.round(diferencaHoras / 24);
  if (diferencaDias === 1) return 'há 1 dia';
  if (diferencaDias < 30) return `há ${diferencaDias} dias`;

  const diferencaMeses = Math.round(diferencaDias / 30);
  if (diferencaMeses < 12) return diferencaMeses === 1 ? 'há 1 mês' : `há ${diferencaMeses} meses`;

  const diferencaAnos = Math.round(diferencaMeses / 12);
  return diferencaAnos === 1 ? 'há 1 ano' : `há ${diferencaAnos} anos`;
}

/** Data e hora absolutas, formatadas por extenso curto — complementa o tempo relativo. */
export function formatarDataHoraAbsoluta(dataIso: string): string {
  const data = new Date(dataIso);
  const dataFormatada = data.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const horaFormatada = data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dataFormatada} ${horaFormatada}`;
}

/** Cabeçalho de seção para o agrupamento por data: "Hoje", "Ontem", ou a data por extenso. */
export function rotuloDoGrupoDeData(dataIso: string): string {
  const data = new Date(dataIso);
  const agora = new Date();
  const inicioHoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const inicioOntem = new Date(inicioHoje);
  inicioOntem.setDate(inicioHoje.getDate() - 1);
  const inicioData = new Date(data.getFullYear(), data.getMonth(), data.getDate());

  if (inicioData.getTime() === inicioHoje.getTime()) return 'Hoje';
  if (inicioData.getTime() === inicioOntem.getTime()) return 'Ontem';

  return data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
}
